import { Button, Flex, Heading, Stack } from '@chakra-ui/react'

import { requestApi } from '../../api/request-handler'
import { ScoringModeProvider, useScoringMode } from '../../contexts/scoring-mode.context'
import type { ClspMode } from '../../types'
import { ModeHeading } from '../mode-heading'

type DownloadsPanelProps = {
  gameId: string
  compId: string | null | undefined
  showAdvancedData: boolean
  isPostMatchCoverageLevel: boolean
}

type DownloadButtonsProps = Omit<DownloadsPanelProps, 'isPostMatchCoverageLevel'>

const triggerDownload = (blob: Blob, filename: string) => {
  const objectUrl = window.URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
}

const downloadApiFile = async (path: string, filename: string, params?: Record<string, string>) => {
  const response = await requestApi(path, { params })

  if (!response.ok) {
    return
  }

  const blob = await response.blob()
  if (blob) {
    triggerDownload(blob, filename)
  }
}

const downloadMatchFile = async (compId: string, mode: ClspMode) => {
  await downloadApiFile(`stats/matches/${compId}`, `comp_${compId}.csv`, { mode })
}

const playerInningsFile = async (gameId: string, mode: ClspMode) => {
  await downloadApiFile(`stats/playerinnings/${gameId}`, `player_innings_${gameId}.csv`, { mode })
}

const teamInningsFile = async (gameId: string, mode: ClspMode) => {
  await downloadApiFile(`stats/teaminnings/${gameId}`, `team_innings_${gameId}.csv`, { mode })
}

const deliveriesFile = async (gameId: string, mode: ClspMode) => {
  await downloadApiFile(`stats/matchballs/${gameId}`, `deliveries_${gameId}.csv`, { mode })
}

const matchWithEnums = async (gameId: string, mode: ClspMode) => {
  await downloadApiFile(`match/${gameId}`, `match_with_enums_${gameId}.json`, { enums: 'true', mode })
}

const ballsWithEnums = async (gameId: string, mode: ClspMode) => {
  await downloadApiFile(`balls/${gameId}`, `ball_with_enums_${gameId}.json`, { enums: 'true', mode })
}

const DownloadButtons = ({ gameId, compId, showAdvancedData }: DownloadButtonsProps) => {
  const mode = useScoringMode()
  return (
    <Flex direction="column">
      {showAdvancedData && <ModeHeading />}
      <Stack spacing={4}>
        <Stack spacing={3} p={4} borderRadius="6px" border="1px solid" borderColor="primary.600">
          <Heading as="h4" fontSize="lg">
            CSV
          </Heading>
          <Stack direction="row" spacing={2}>
            {compId && <Button onClick={() => downloadMatchFile(compId, mode)}>Match File</Button>}
            <Button onClick={() => playerInningsFile(gameId, mode)}>Player Innings</Button>
            <Button onClick={() => teamInningsFile(gameId, mode)}>Team Innings</Button>
            <Button onClick={() => deliveriesFile(gameId, mode)}>Deliveries</Button>
          </Stack>
        </Stack>
        <Stack spacing={3} p={4} borderRadius="6px" border="1px solid" borderColor="primary.600">
          <Heading as="h4" fontSize="lg">
            JSON
          </Heading>
          <Stack direction="row" spacing={2}>
            <Button onClick={() => matchWithEnums(gameId, mode)}>Match with Enums</Button>
            <Button onClick={() => ballsWithEnums(gameId, mode)}>Balls with Enums</Button>
          </Stack>
        </Stack>
      </Stack>
    </Flex>
  )
}

export const DownloadsPanel = ({ gameId, compId, showAdvancedData, isPostMatchCoverageLevel }: DownloadsPanelProps) => {
  return (
    <Stack direction="row" spacing={6}>
      {!isPostMatchCoverageLevel && (
        <ScoringModeProvider mode="core">
          <DownloadButtons gameId={gameId} compId={compId} showAdvancedData={showAdvancedData} />
        </ScoringModeProvider>
      )}
      {showAdvancedData && (
        <ScoringModeProvider mode="advanced">
          <DownloadButtons gameId={gameId} compId={compId} showAdvancedData={showAdvancedData} />
        </ScoringModeProvider>
      )}
    </Stack>
  )
}
